import {
  types,
  type Instance,
  getParent,
  getRoot,
  flow,
} from 'mobx-state-tree';

import { TRACKING_CANDIDATE_DETAILS } from 'src/constants/pageTrackingData';
import { trackAddedNote, trackDeletedNote } from 'src/utils/events';

import createApplicationNote from '../../graphql/hirer/createApplicationNote';
import fetchApplicationNotes from '../../graphql/hirer/fetchApplicationNotes';
import removeApplicationNote from '../../graphql/hirer/removeApplicationNote';
import { logError } from '../../utils/logError';
import type { IStore } from '../Store';
import {
  STATE_ERROR,
  STATE_LOADED,
  stateType,
  ACTION_ERROR,
  ACTION_DONE,
} from '../state';

import type { IJobApplication } from './JobApplication';
import Note from './Note';

const Notes = types
  .model('Notes', {
    noteList: types.array(Note),
    noteCount: types.optional(types.number, 0),
    state: stateType,
  })
  .views((self) => ({
    get rootStore() {
      return getRoot(self) as IStore;
    },

    get jobApplication(): IJobApplication {
      return getParent(self) as IJobApplication;
    },

    get trackingAttributes(): {
      candidateId: string;
      currentPage: typeof TRACKING_CANDIDATE_DETAILS;
    } {
      return {
        ...(this.jobApplication as IJobApplication).baseTrackingAttributes,
        currentPage: TRACKING_CANDIDATE_DETAILS,
      };
    },
  }))
  .actions((self) => {
    const loadNotes: any = flow(function* () {
      try {
        const notes = yield fetchApplicationNotes(
          (self.jobApplication as IJobApplication).id,
        );
        self.noteList = notes;
        self.noteCount = notes.length;
        self.state = STATE_LOADED;
      } catch (error: any) {
        self.state = STATE_ERROR;
        logError(
          {
            errorCode: 'LOAD_NOTES',
            description: 'Error attempting: queryApplicationNotes',
          },
          {
            error,
            jobApplicationId: (self.jobApplication as IJobApplication).id,
          },
        );
      }
    });

    const removeNote: any = flow(function* (noteId: string) {
      try {
        yield removeApplicationNote(
          (self.jobApplication as IJobApplication).id,
          noteId,
        );
        self.noteList = self.noteList.filter(
          (note) => note.id !== noteId,
        ) as any;
        self.noteCount = self.noteList.length;
        trackDeletedNote(self.trackingAttributes);
        return ACTION_DONE;
      } catch (error: any) {
        logError(
          {
            errorCode: 'REMOVE_NOTE',
            description: 'Error attempting: removeApplicationNote',
          },
          {
            error,
            jobApplicationId: (self.jobApplication as IJobApplication).id,
            noteId,
          },
        );
        return ACTION_ERROR;
      }
    });

    const addNote: any = flow(function* (content: string) {
      try {
        const createdNote = yield createApplicationNote(
          (self.jobApplication as IJobApplication).id,
          content,
        );
        self.noteList.unshift(createdNote);
        self.noteCount = self.noteList.length;
        trackAddedNote(self.trackingAttributes);
        return ACTION_DONE;
      } catch (error: any) {
        logError(
          {
            errorCode: 'CREATE_NOTE',
            description: 'Error attempting: createApplicationNote',
          },
          {
            error,
            jobApplicationId: (self.jobApplication as IJobApplication).id,
          },
        );
        return ACTION_ERROR;
      }
    });

    return { loadNotes, addNote, removeNote };
  });

export interface INotes extends Instance<typeof Notes> {}

export default Notes;
